import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Admin.css';
import { useTranslation } from 'react-i18next';
import { updateInstructorWithGalleryImages } from '../../services/uploadService';

const InstructorForm = ({ initialData, onSubmit, onCancel }) => {
  const { t, i18n } = useTranslation();
  const galleryFileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    bio: '',
    image_url: '',
    specialty: ''
  });
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  // Separate state for existing and new gallery images
  const [selectedGalleryImages, setSelectedGalleryImages] = useState([]);
  const [existingGalleryUrls, setExistingGalleryUrls] = useState([]);
  const [newGalleryPreviewUrls, setNewGalleryPreviewUrls] = useState([]);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  const [replaceGalleryImages, setReplaceGalleryImages] = useState(false);
  
  // Force re-render on language change
  useEffect(() => {
    // This empty effect depends on i18n.language
    // and will trigger a re-render when language changes
  }, [i18n.language]);
  
  // Initialize form with selected instructor data if editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        title: initialData.title || '',
        bio: initialData.bio || '',
        image_url: initialData.image_url || '',
        specialty: initialData.specialty || ''
      });
      
      // Set image preview if image_url exists
      if (initialData.image_url) {
        setPreviewUrl(initialData.image_url);
      }
      
      // Set existing gallery previews if support_images exists
      if (initialData.support_images && Array.isArray(initialData.support_images)) {
        setExistingGalleryUrls(initialData.support_images);
      }
    } else {
      // Reset form for new instructor
      setFormData({
        name: '',
        title: '',
        bio: '',
        image_url: '',
        specialty: ''
      });
      setPreviewUrl('');
      setExistingGalleryUrls([]);
      setNewGalleryPreviewUrls([]);
    }
  }, [initialData]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle main image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle gallery images selection
  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 0) {
      setSelectedGalleryImages(files);
      
      // Create preview URLs for the selected images
      const newPreviewUrls = files.map(file => URL.createObjectURL(file));
      
      // Clean up any previous new preview URLs
      newGalleryPreviewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      // Set the new preview URLs (not replacing existing gallery URLs)
      setNewGalleryPreviewUrls(newPreviewUrls);
    }
  };
  
  // Remove a new gallery image
  const removeGalleryImage = (index) => {
    const updatedImages = [...selectedGalleryImages];
    const updatedPreviews = [...newGalleryPreviewUrls];
    
    // Revoke the object URL to avoid memory leaks if it's a blob URL
    if (updatedPreviews[index] && updatedPreviews[index].startsWith('blob:')) {
      URL.revokeObjectURL(updatedPreviews[index]);
    }
    
    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);
    
    setSelectedGalleryImages(updatedImages);
    setNewGalleryPreviewUrls(updatedPreviews);
  };
  
  // Open file selector for gallery images
  const openGalleryFileSelector = () => {
    galleryFileInputRef.current.click();
  };
  
  // Upload the main image file
  const uploadImage = async () => {
    if (!selectedImage) return false;
    
    setIsUploading(true);
    setUploadError('');
    
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'instructors/upload-image',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setIsUploading(false);
      return response.data.image_url;
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadError(t('admin.failedInstructorSave'));
      setIsUploading(false);
      return false;
    }
  };
  
  // Upload multiple gallery images
  const uploadGalleryImages = async () => {
    if (!selectedGalleryImages || selectedGalleryImages.length === 0) {
      return [];
    }
    
    setIsUploadingGallery(true);
    setUploadError('');
    
    try {
      const galleryFormData = new FormData();
      selectedGalleryImages.forEach((image, index) => {
        galleryFormData.append('gallery_images', image);
      });
      
      // Add instructor ID if we're in edit mode
      if (initialData?.id) {
        galleryFormData.append('instructor_id', initialData.id);
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'instructors/upload-gallery',
        galleryFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setIsUploadingGallery(false);
      return response.data.image_urls;
    } catch (err) {
      console.error('Error uploading gallery images:', err);
      setUploadError(t('admin.failedInstructorSave'));
      setIsUploadingGallery(false);
      return [];
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // First upload the image if a new one is selected
    let imageUrl = formData.image_url;
    
    if (selectedImage) {
      const uploadedUrl = await uploadImage();
      if (!uploadedUrl) {
        // If upload failed, stop the submission
        return;
      }
      imageUrl = uploadedUrl;
    }
    
    // Create the updated data object
    const updatedData = {
      ...formData,
      image_url: imageUrl,
    };
    
    // Make sure we're passing the ID when updating an existing instructor
    if (initialData?.id) {
      updatedData.id = initialData.id;
      console.log("Ensuring instructor ID is included:", updatedData.id);
    }
    
    // UPDATING EXISTING INSTRUCTOR WITH GALLERY CHANGES
    if (initialData?.id && (selectedGalleryImages.length > 0 || replaceGalleryImages)) {
      try {
        setIsUploadingGallery(true);
        console.log("Updating instructor with ID and gallery changes:", updatedData.id);
        
        const result = await updateInstructorWithGalleryImages(
          updatedData, 
          selectedGalleryImages, 
          replaceGalleryImages
        );
        
        setIsUploadingGallery(false);
        
        // Pass the result to the parent component
        onSubmit(result);
        return;
      } catch (error) {
        console.error('Error updating instructor with gallery images:', error);
        setUploadError(t('admin.failedInstructorSave'));
        setIsUploadingGallery(false);
        return;
      }
    }
    
    // SIMPLE UPDATE (NO GALLERY CHANGES) OR CREATING WITHOUT GALLERY
    if (initialData?.id || !selectedGalleryImages.length) {
      console.log("Simple update or create without gallery images");
      // For existing instructor without gallery changes, or new instructor without gallery
      // Just pass the data object to the parent
      
      // If we're in edit mode and have support_images, make sure to include them
      if (initialData?.id && initialData.support_images) {
        updatedData.support_images = initialData.support_images;
      }
      
      onSubmit(updatedData);
      return;
    }
    
    // CREATING NEW INSTRUCTOR WITH GALLERY IMAGES
    try {
      setIsUploadingGallery(true);
      console.log("Creating new instructor with gallery images");
      
      // First create the instructor without gallery images
      const token = localStorage.getItem('token');
      
      // Step 1: Create the instructor to get an ID
      const createResponse = await axios.post(
        'instructors',
        updatedData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      const newInstructorId = createResponse.data.instructor_id;
      console.log("New instructor created with ID:", newInstructorId);
      
      // Step 2: If we have gallery images, upload them to the instructor's folder
      if (selectedGalleryImages.length > 0) {
        const galleryFormData = new FormData();
        
        // Add the new instructor ID
        galleryFormData.append('instructor_id', newInstructorId);
        
        // Add the gallery images
        selectedGalleryImages.forEach(image => {
          galleryFormData.append('gallery_images', image);
        });
        
        // Upload the gallery images
        const galleryResponse = await axios.post(
          'instructors/upload-gallery',
          galleryFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Step 3: Now update the instructor with the gallery image URLs
        if (galleryResponse.data.image_urls && galleryResponse.data.image_urls.length > 0) {
          const updateData = {
            ...updatedData,
            support_images: galleryResponse.data.image_urls
          };
          
          await axios.put(
            `instructors/${newInstructorId}`,
            updateData,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            }
          );
        }
      }
      
      setIsUploadingGallery(false);
      onSubmit(createResponse);
      
    } catch (error) {
      console.error('Error creating instructor with gallery images:', error);
      setUploadError(t('admin.failedInstructorSave'));
      setIsUploadingGallery(false);
    }
  };
  
  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      // Clean up main preview URL
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      
      // Clean up gallery preview URLs
      newGalleryPreviewUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previewUrl, newGalleryPreviewUrls]);
  
  return (
    <div className="instructor-form">
      <h3>{initialData ? t('admin.editInstructor') : t('admin.addNewInstructor')}</h3>
      
      {uploadError && <div className="error-message">{uploadError}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">{t('admin.instructorName')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('admin.instructorName')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="title">{t('admin.title')}</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder={t('admin.instructorTitle')}
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="specialty">{t('admin.specialty')}</label>
            <input
              type="text"
              id="specialty"
              name="specialty"
              value={formData.specialty}
              onChange={handleChange}
              placeholder={t('admin.specialtyPlaceholder')}
              required
            />
          </div>
          
          <div className="form-group image-upload-group">
            <label htmlFor="image">{t('admin.profileImage')}</label>
            <div className="profile-image-upload">
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleImageChange}
                accept=".jpg, image/jpeg"
                className="file-input"
              />
              
              {previewUrl && (
                <div className="image-preview">
                  <img 
                    src={previewUrl} 
                    alt={t('admin.preview')} 
                    className="preview-thumbnail" 
                  />
                </div>
              )}
            </div>
            
            {isUploading && <div className="upload-loading">{t('admin.uploading')}</div>}
          </div>
        </div>
        
        <div className="form-group full-width">
          <label htmlFor="bio">{t('admin.biography')}</label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            rows="6"
            placeholder={t('admin.biographyPlaceholder')}
            required
            style={{ whiteSpace: 'pre-wrap' }}
          />
          {/* <small className="form-text">
            Enter your text with line breaks and spacing as you want it to appear. The text formatting will be preserved.
          </small> */}
        </div>
        
        {/* Gallery Images Section */}
        <div className="form-group full-width image-upload-section">
          <label>{t('admin.galleryImages')}</label>
          
          {/* Existing gallery images (if in edit mode) */}
          {initialData && existingGalleryUrls.length > 0 && (
            <div className="existing-gallery">
              <h4>{t('admin.currentGalleryImages')}{replaceGalleryImages && ` (${t('admin.willBeReplaced')})`}</h4>
              <div className="gallery-preview-container">
                {existingGalleryUrls.map((url, index) => (
                  <div key={index} className="gallery-preview-item">
                    <img 
                      src={url} 
                      alt={`${t('admin.gallery')} ${index + 1}`} 
                      className={`gallery-thumbnail ${replaceGalleryImages ? 'to-be-replaced' : ''}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="upload-container mobile-friendly">
            <input
              type="file"
              ref={galleryFileInputRef}
              onChange={handleGalleryImagesChange}
              accept=".jpg, image/jpeg"
              multiple
              style={{ display: 'none' }}
            />
            <button 
              type="button" 
              className="upload-button"
              onClick={openGalleryFileSelector}
            >
              {t('admin.selectGalleryImages')}
            </button>
            <span className="upload-hint">
              {t('admin.galleryImagesHint')} (.jpg prosim, max velkost kazdej photo 3MB)
            </span>
          </div>
          
          {/* Selected new gallery images preview */}
          {selectedGalleryImages.length > 0 && (
            <div className="selected-gallery-preview">
              <h4>{t('admin.newImagesToUpload')} <span className="count">({selectedGalleryImages.length})</span></h4>
              <div className="gallery-preview-container">
                {selectedGalleryImages.map((file, index) => (
                  <div key={index} className="gallery-preview-item">
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt={`${t('admin.preview')} ${index + 1}`} 
                      className="gallery-thumbnail"
                    />
                    <button 
                      type="button" 
                      className="remove-image" 
                      onClick={() => removeGalleryImage(index)}
                      aria-label={t('admin.remove')}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Replace images option */}
          {initialData && initialData.support_images && initialData.support_images.length > 0 && (
            <div className="replace-images-option">
              <label htmlFor="replace-gallery-images" className={replaceGalleryImages ? 'replace-active' : ''}>
                <input
                  type="checkbox"
                  id="replace-gallery-images"
                  checked={replaceGalleryImages}
                  onChange={(e) => setReplaceGalleryImages(e.target.checked)}
                />
                {t('admin.replaceExistingGallery')}
              </label>
              <div className="replace-description">
                {replaceGalleryImages 
                  ? t('admin.replaceGalleryDescription') 
                  : t('admin.addGalleryDescription')}
              </div>
            </div>
          )}
          
          {isUploadingGallery && <div className="upload-loading">{t('admin.uploadingGallery')}</div>}
        </div>
        
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button 
            type="submit" 
            className="save-button"
            disabled={isUploading || isUploadingGallery}
          >
            {initialData ? t('admin.updateInstructor') : t('admin.createInstructor')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InstructorForm; 